import React, { useEffect, useRef, useContext, useMemo } from "react";
import { PropertyContext } from "../search/context/property-context-v2";
import { renderToString } from "react-dom/server";
import mapboxgl from "mapbox-gl";
import PopupContent from "../search/components/PopupContent";
import OvalMarker from "../components/icons/OvalMarker";
import "mapbox-gl/dist/mapbox-gl.css";
import "../search/styles/map.css";

const MAPBOX_STYLE = "mapbox://styles/mapbox/streets-v11";
const DEFAULT_CENTER_LNG_LAT: [number, number] = [-73.064034, 41.230698];
const DEFAULT_ZOOM = 13;
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 50;
const MOBILE_POPUP_OFFSET_Y = 220;
const MAPBOX_TILE_SIZE = 512;

interface Property {
  long: number;
  lat: number;
  selling_price: string;
  [key: string]: any;
}

interface Filters {
  zoom: number;
  ne: string;
  sw: string;
  city: string;
  state: string;
  [key: string]: any;
}

interface Meta {
  boundary: GeoJSON.Geometry;
}

interface PropertyContextType {
  properties: Property[];
  filters: Filters;
  setFilters: (filters: Filters) => void;
  meta: Meta;
}

let cachedLeaflet: any = null;
const loadLeaflet = async () => {
  if (!cachedLeaflet) {
    await import("leaflet/dist/leaflet.css");
    const leafletModule = await import("leaflet");
    cachedLeaflet = leafletModule.default || leafletModule;
  }
  return cachedLeaflet;
};

const useMap = (center = null) => {
  // Check WebGL support once on mount
  const isMapboxSupported = useMemo(() => mapboxgl.supported(), []);

  const mapRef = useRef<mapboxgl.Map | null>(null);
  const leafletMapRef = useRef<any>(null);
  const leafletMarkersRef = useRef<any>(null);
  const leafletBoundaryRef = useRef<any>(null);

  const { properties, filters, setFilters, meta } = useContext(
    PropertyContext,
  ) as PropertyContextType;

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary-color")
    .trim();

  const getPlaceFilter = () => {
    const params = new URLSearchParams(window.location.search);
    let state = params.get("state") || "";
    let city = params.get("city") || "";
    if (city === "null") city = "";
    if (state === "null") state = "";
    return { city, state };
  };

  const handleMapMoveEnd = () => {
    let zoom, sw, ne;

    if (isMapboxSupported && mapRef.current) {
      zoom = mapRef.current.getZoom();
      const bounds = mapRef.current.getBounds();
      sw = `${bounds.getSouthWest().lng},${bounds.getSouthWest().lat}`;
      ne = `${bounds.getNorthEast().lng},${bounds.getNorthEast().lat}`;
    } else if (!isMapboxSupported && leafletMapRef.current) {
      zoom = leafletMapRef.current.getZoom();
      const bounds = leafletMapRef.current.getBounds();
      sw = `${bounds.getSouthWest().lng},${bounds.getSouthWest().lat}`;
      ne = `${bounds.getNorthEast().lng},${bounds.getNorthEast().lat}`;
    } else {
      return;
    }

    const { city, state } = getPlaceFilter();
    setFilters((prevFilters) => ({ ...prevFilters, ne, sw, zoom, city, state }));
  };

  // ==========================================
  // MAPBOX IMPLEMENTATION
  // ==========================================

  const initMapbox = () => {
    mapboxgl.accessToken = document.querySelector('meta[name="mapboxToken"]')?.content;
    const ovalSvg = renderToString(<OvalMarker primaryColor={primaryColor} />);
    const ovalUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(ovalSvg)}`;

    const map = new mapboxgl.Map({
      container: "map-container",
      style: MAPBOX_STYLE,
      center: center || DEFAULT_CENTER_LNG_LAT,
      zoom: DEFAULT_ZOOM,
      dragPan: true,
    });

    mapRef.current = map;

    map.on("load", () => {
      const img = new Image();
      img.onload = () => {
        map.addImage("oval-marker", img);

        map.addSource("properties", {
          type: "geojson",
          data: getPropertiesGeoJSON(),
          cluster: true,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
          clusterRadius: CLUSTER_RADIUS,
        });

        addMapboxClusterLayers();

        const navControl = new mapboxgl.NavigationControl({
          showCompass: false,
          showZoom: true,
        });
        map.addControl(navControl, "top-right");

        addMapboxEventListeners();
        handleMapMoveEnd();

        // Catch up on boundary state if it loaded before map finished init
        if (meta.boundary) updateMapboxBoundary();
      };
      img.src = ovalUrl;
    });
  };

  const updateMapboxBoundary = () => {
    if (!mapRef.current?.isStyleLoaded()) return;

    if (mapRef.current.getLayer("outline")) mapRef.current.removeLayer("outline");
    if (mapRef.current.getSource("place")) mapRef.current.removeSource("place");

    if (meta.boundary && filters.city) {
      mapRef.current.addSource("place", {
        type: "geojson",
        data: { type: "Feature", geometry: meta.boundary },
      });

      mapRef.current.addLayer({
        id: "outline",
        type: "line",
        source: "place",
        layout: {},
        paint: { "line-color": primaryColor, "line-width": 3 },
      });
    }
  };

  const getPropertiesGeoJSON = () => ({
    type: "FeatureCollection",
    features: properties.map((property) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [property.long, property.lat] },
      properties: { ...property },
    })) as GeoJSON.Feature[], // Type cast for Mapbox GeoJSON source
  });

  const addMapboxClusterLayers = () => {
    if (!mapRef.current) return;

    mapRef.current.addLayer({
      id: "clusters",
      type: "circle",
      source: "properties",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": primaryColor,
        "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
      },
    });

    mapRef.current.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "properties",
      filter: ["has", "point_count"],
      layout: { "text-field": "{point_count_abbreviated}", "text-size": 12 },
      paint: { "text-color": "#fff" },
    });

    mapRef.current.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "properties",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "oval-marker",
        "icon-size": 1,
        "icon-allow-overlap": true,
        "icon-anchor": "center",
      },
    });

    mapRef.current.addLayer({
      id: "unclustered-point-label",
      type: "symbol",
      source: "properties",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-field": ["concat", ["get", "selling_price"]],
        "text-size": 13,
        "text-allow-overlap": true,
        "text-anchor": "center",
        "text-offset": [0, 0],
      },
      paint: {
        "text-color": primaryColor,
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
      },
    });
  };

  const addMapboxEventListeners = () => {
    if (!mapRef.current) return;

    mapRef.current.on("click", "clusters", (e: any) => {
      const features = mapRef.current!.queryRenderedFeatures(e.point, {
        layers: ["clusters"],
      });
      const clusterId = features[0].properties!.cluster_id;
      const source = mapRef.current!.getSource("properties") as mapboxgl.GeoJSONSource;

      source.getClusterExpansionZoom(clusterId, (err: any, zoom: any) => {
        if (err) return;
        mapRef.current!.easeTo({
          center: (features[0].geometry as any).coordinates,
          zoom,
        });
      });
    });

    mapRef.current.on("click", "unclustered-point", (e: any) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const property = e.features[0].properties;

      new mapboxgl.Popup({
        closeButton: false,
        className: "property-popup",
        offset: [0, -15],
        maxWidth: "240px",
      })
        .setLngLat(coordinates)
        .setHTML(
          renderToString(<PopupContent property={property} primaryColor={primaryColor} />),
        )
        .addTo(mapRef.current!);

      if (window.innerWidth < 640 && mapRef.current) {
        const point = mapRef.current.project(coordinates);
        const newPoint = { x: point.x, y: point.y + MOBILE_POPUP_OFFSET_Y };
        const newCenter = mapRef.current.unproject(newPoint);
        mapRef.current.easeTo({ center: newCenter, duration: 300 });
      }
    });

    const setCursor = (cursor: string) => () => {
      if (mapRef.current) mapRef.current.getCanvas().style.cursor = cursor;
    };

    mapRef.current.on("mouseenter", "clusters", setCursor("pointer"));
    mapRef.current.on("mouseleave", "clusters", setCursor(""));
    mapRef.current.on("mouseenter", "unclustered-point", setCursor("pointer"));
    mapRef.current.on("mouseleave", "unclustered-point", setCursor(""));
  };

  // ==========================================
  // LEAFLET FALLBACK IMPLEMENTATION
  // ==========================================

  const initLeaflet = async () => {
    const L = await loadLeaflet();
    const mapCenter = center ? [center.lat, center.lng] : [DEFAULT_CENTER_LNG_LAT[1], DEFAULT_CENTER_LNG_LAT[0]];

    const map = L.map("map-container", {
      zoomControl: true,
    }).setView(mapCenter as [number, number], DEFAULT_ZOOM);

    const mapboxToken = document.querySelector('meta[name="mapboxToken"]')?.content;

    // Use Mapbox static tiles to mimic Mapbox visual style
    L.tileLayer(
      `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/${MAPBOX_TILE_SIZE}/{z}/{x}/{y}?access_token=${mapboxToken}`,
      {
        attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a>',
        tileSize: MAPBOX_TILE_SIZE,
        zoomOffset: -1,
        maxZoom: DEFAULT_ZOOM + 5,
      }
    ).addTo(map);

    leafletMapRef.current = map;

    // Add layer group to cleanly mount/unmount markers dynamically
    leafletMarkersRef.current = L.layerGroup().addTo(map);

    map.on("moveend", handleMapMoveEnd);
    handleMapMoveEnd();

    if (meta.boundary) updateLeafletBoundary();
    if (properties.length) updateLeafletProperties();
  };

  const updateLeafletBoundary = async () => {
    if (!leafletMapRef.current) return;
    const L = await loadLeaflet();

    if (leafletBoundaryRef.current) {
      leafletBoundaryRef.current.remove();
      leafletBoundaryRef.current = null;
    }

    if (meta.boundary && filters.city) {
      leafletBoundaryRef.current = L.geoJSON(meta.boundary, {
        style: { color: primaryColor, weight: 3, fillOpacity: 0 },
      }).addTo(leafletMapRef.current);
    }
  };

  const updateLeafletProperties = async () => {
    if (!leafletMapRef.current || !leafletMarkersRef.current) return;
    const L = await loadLeaflet();

    leafletMarkersRef.current.clearLayers();

    properties.forEach((property) => {
      // Create a pure CSS pill shape, bypassing SVG layering issues entirely
      const markerHtml = `
        <div style="
          background-color: white;
          color: ${primaryColor};
          border-radius: 14px;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 500;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15);
          white-space: nowrap;
        ">
          ${property.selling_price || ""}
        </div>
      `;

      const customIcon = L.divIcon({
        className: "custom-leaflet-marker",
        html: markerHtml,
        iconSize: [80, 28], // Matches the exact dimensions of your original SVG
        iconAnchor: [40, 14], // Centered (half of 80x28)
        popupAnchor: [0, -14],
      });

      const popupHtml = renderToString(
        <PopupContent property={property} primaryColor={primaryColor} />
      );

      L.marker([property.lat, property.long], { icon: customIcon })
        .bindPopup(popupHtml, { className: "property-popup", closeButton: false })
        .addTo(leafletMarkersRef.current);
    });
  };

  // ==========================================
  // LIFECYCLE EFFECTS
  // ==========================================

  // 1. Initialization
  useEffect(() => {
    if (isMapboxSupported) {
      initMapbox();
    } else {
      initLeaflet();
    }

    return () => {
      if (mapRef.current) mapRef.current.remove();
      if (leafletMapRef.current) leafletMapRef.current.remove();
    };
  }, []);

  // 2. Track Filter Events
  useEffect(() => {
    if (isMapboxSupported && mapRef.current) {
      mapRef.current.on("moveend", handleMapMoveEnd);
    } // Leaflet handles its own event tracking post-initialization

    return () => {
      if (isMapboxSupported && mapRef.current) {
        mapRef.current.off("moveend", handleMapMoveEnd);
      }
    };
  }, [filters]);

  // 3. Keep properties in sync
  useEffect(() => {
    if (isMapboxSupported && mapRef.current) {
      if (mapRef.current.isStyleLoaded()) {
        const source = mapRef.current.getSource("properties") as mapboxgl.GeoJSONSource;
        if (source) source.setData(getPropertiesGeoJSON());
      }
    } else if (!isMapboxSupported && leafletMapRef.current) {
      updateLeafletProperties();
    }
  }, [properties]);

  // 4. Handle boundary outlines
  useEffect(() => {
    if (isMapboxSupported) {
      updateMapboxBoundary();
    } else {
      updateLeafletBoundary();
    }
  }, [meta, filters.city]);

  // 5. Handle external map centering
  useEffect(() => {
    if (!center) return;

    if (isMapboxSupported && mapRef.current) {
      mapRef.current.panTo(center);
    } else if (!isMapboxSupported && leafletMapRef.current) {
      leafletMapRef.current.panTo([center[1], center[0]]);
    }
  }, [center]);

  // 6. Set external zoom
  useEffect(() => {
    if (isMapboxSupported && mapRef.current?.isStyleLoaded()) {
      mapRef.current.setZoom(filters.zoom);
    } else if (!isMapboxSupported && leafletMapRef.current) {
      leafletMapRef.current.setZoom(filters.zoom);
    }
  }, [filters.zoom]);

  return isMapboxSupported ? mapRef.current : leafletMapRef.current;
};

export default useMap;
